<template>
  <div
    class="bg-neutral-100 w-full h-[220px] shared-picture-carousel"
    :class="{ 'is-nested-carousel': nestedCarousel }"
  >
    <UiCarousel
      class="h-full"
      :slide-size="100"
      :slide-count="total"
      :show-arrows="total > 1"
      :options="carouselOptions"
      :accessibility="accessibilityOptions"
      :keyboard="total > 1"
      defer-until-visible
      dots-selector=""
      @select="onSelect"
    >
      <div
        v-for="(bild, index) in props.item?.bilder_neu"
        :key="`slide-${index}`"
        class="embla__slide h-full"
      >
        <img
          v-if="isSlideHydrated(index)"
          :src="bild"
          :loading="!lazyLoading && index === 0 ? 'eager' : 'lazy'"
          :fetchpriority="!lazyLoading && index === 0 ? 'high' : 'auto'"
          width="405"
          height="220"
          :alt="props.item?.alt || `Image ${index + 1}`"
          class="object-cover object-center h-full w-full bg-neutral-50"
          decoding="async"
        >
        <div
          v-else
          class="h-full w-full bg-neutral-50"
          aria-hidden="true"
        />
      </div>

      <template
        v-if="total > 1"
        #prev
      >
        <span aria-hidden="true" />
      </template>
      <template
        v-if="total > 1"
        #next
      >
        <span aria-hidden="true" />
      </template>

      <template
        v-if="total > 1"
        #dots
      >
        <div
          class="embla-pagination-picture-carousel"
          aria-hidden="true"
        >
          <div
            class="embla__dots-inner embla-pagination-bullets-inner"
            :style="{ transform: `translateX(${innerOffsetNavigationBullets}px)` }"
          >
            <span
              v-for="(_, i) in total"
              :key="i"
              class="embla-pagination-bullet"
              :class="{ 'embla-pagination-bullet-active': i === currentIndex }"
            />
          </div>
        </div>
      </template>
    </UiCarousel>
  </div>
</template>

<script setup lang="ts">
import type { EmblaOptionsType } from 'embla-carousel'
import type { AccessibilityOptionsType } from 'embla-carousel-accessibility'

interface CarouselItem {
  bilder_neu?: string[]
  alt?: string
}

interface Props {
  item?: CarouselItem | null
  lazyLoading?: boolean
  nestedCarousel?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  item: null,
  lazyLoading: true,
  nestedCarousel: false
})

const { translate: t } = useTranslations('SharedPictureCarousel')

const currentIndex = ref(0)
const total = computed(() => props.item?.bilder_neu?.length ?? 0)

/** Slides that already got a real `src` – never unload once hydrated. */
const hydratedSlides = ref(new Set<number>())
const hasInteracted = ref(false)

const hydrateSlides = (index: number, includeNeighbors: boolean): void => {
  const len = total.value
  if (len === 0) {
    return
  }

  const next = new Set(hydratedSlides.value)
  next.add(0)
  next.add(index)

  if (includeNeighbors && len > 1) {
    next.add((index - 1 + len) % len)
    next.add((index + 1) % len)
  }

  hydratedSlides.value = next
}

const isSlideHydrated = (index: number): boolean => hydratedSlides.value.has(index)

watch(total, () => {
  hasInteracted.value = false
  currentIndex.value = 0
  hydratedSlides.value = new Set()
  hydrateSlides(0, false)
}, { immediate: true })

// bullet size (6px) + 2 * horizontal gap (3px) = 12px
const bulletStep = 12

const innerOffsetNavigationBullets = computed(() => {
  const offset = -(currentIndex.value - 2) * bulletStep
  const minOffset = -(Math.max(total.value - 5, 0)) * bulletStep
  return Math.min(0, Math.max(offset, minOffset))
})

const carouselOptions = computed<EmblaOptionsType>(() => ({
  align: 'start',
  loop: total.value > 1
}))

const accessibilityOptions = computed<AccessibilityOptionsType>(() => ({
  announceChanges: true,
  carouselAriaLabel: props.item?.alt || t.value('carousel_aria_label'),
  previousButtonAriaLabel: t.value('previous_button_aria_label'),
  nextButtonAriaLabel: t.value('next_button_aria_label'),
  slideAriaLabel: (_grouped, first) => t.value('slide_aria_label', { current: first + 1, total: total.value }),
  dotButtonAriaLabel: (_grouped, first) => t.value('dot_button_aria_label', { current: first + 1, total: total.value }),
  liveRegionContent: (_grouped, first) => t.value('live_region_content', { current: first + 1, total: total.value }),
  rootNode: (emblaRoot) => emblaRoot.parentElement
}))

const onSelect = (index: number): void => {
  if (index !== currentIndex.value) {
    hasInteracted.value = true
  }
  currentIndex.value = index
  hydrateSlides(index, hasInteracted.value)
}
</script>

<style scoped src="./css/SharedPictureCarousel.css"></style>
